import { useEffect, useMemo, useState } from "react";
import { api, formatApiError, inr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bank } from "@phosphor-icons/react";
import ChallanTdsPanel from "@/components/ChallanTdsPanel";
import ChallanGstPanel from "@/components/ChallanGstPanel";

/**
 * ChallanPostDialog — thin orchestrator for the "post a bank line as a
 * challan payment" flow. Owns cross-tab state (kind, common metadata,
 * submit lifecycle) and delegates the TDS / GST forms to their own
 * focused panel components.
 *
 * Props:
 *   open, onClose, onPosted(res)   — modal control
 *   line: the bank line being settled   — reads `debit` + `date`
 */
export default function ChallanPostDialog({ open, onClose, line, onPosted }) {
    const [kind, setKind] = useState("tds");
    const [busy, setBusy] = useState(false);
    const debit = Number(line?.debit || 0);

    // Metadata common to both tabs
    const [challanNo, setChallanNo] = useState("");
    const [bsrCode, setBsrCode] = useState("");
    const [cin, setCin] = useState("");
    const [notes] = useState("");                    // reserved — no UI yet

    // TDS-specific state
    const [tdsSection, setTdsSection] = useState("194C-1");
    const [tdsPeriod, setTdsPeriod] = useState(defaultPeriod(line?.date));
    const [ay, setAy] = useState(defaultAy(line?.date));
    const [tdsOut, setTdsOut] = useState(null);

    // GST-specific state
    const [gstPeriod, setGstPeriod] = useState(defaultPeriod(line?.date));
    const [cpin, setCpin] = useState("");
    const [gstPreview, setGstPreview] = useState(null);
    const [previewBusy, setPreviewBusy] = useState(false);

    // Reset each time we open, fetch TDS outstanding upfront
    useEffect(() => {
        if (!open) return;
        setKind("tds");
        setChallanNo(""); setBsrCode(""); setCin("");
        setTdsSection("194C-1");
        setTdsPeriod(defaultPeriod(line?.date));
        setAy(defaultAy(line?.date));
        setGstPeriod(defaultPeriod(line?.date));
        setCpin("");
        setGstPreview(null);
        (async () => {
            try {
                const { data } = await api.get("/challans/tds/outstanding");
                setTdsOut(data);
            } catch (_e) { /* silent */ }
        })();
    }, [open, line?.id, line?.date]);

    // Load GST preview whenever GST tab active + month changes
    useEffect(() => {
        if (!open || kind !== "gst") return;
        setPreviewBusy(true); setGstPreview(null);
        (async () => {
            try {
                const { data } = await api.get("/challans/gst/preview", { params: { month: gstPeriod }});
                setGstPreview(data);
            } catch (e) { toast.error(formatApiError(e)); }
            finally { setPreviewBusy(false); }
        })();
    }, [open, kind, gstPeriod]);

    // Derived values passed to panels
    const tdsSectionOptions = useMemo(() => (tdsOut?.sections || []).map((s) => ({
        section: s.section, label: s.label, outstanding: s.outstanding,
    })), [tdsOut]);
    const selectedTdsSectionInfo = tdsSectionOptions.find((s) => s.section === tdsSection);
    const gstCash    = gstPreview?.cash_to_pay?.total ?? 0;
    const gstDiff    = Math.abs(debit - gstCash);
    const gstMatches = gstPreview ? gstDiff <= 1 : false;

    // Submitters
    const postChallan = async (payloadChallan, successMsg) => {
        setBusy(true);
        try {
            const res = await api.post(`/bank-lines/${line.id}/confirm`, {
                challan_type: kind, challan: payloadChallan,
            });
            toast.success(`${successMsg} — ₹${inr(res.data.challan.amount)}`);
            onPosted?.(res.data);
            onClose?.();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    const postTds = () => postChallan({
        section: tdsSection, period_month: tdsPeriod,
        challan_no: challanNo || null, bsr_code: bsrCode || null,
        cin: cin || null, assessment_year: ay || null,
        notes: notes || null,
    }, "TDS challan posted");

    const postGst = () => postChallan({
        period_month: gstPeriod,
        challan_no: challanNo || null, cpin: cpin || null,
        bsr_code: bsrCode || null, cin: cin || null,
        notes: notes || null,
    }, "GST challan posted");

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
            <DialogContent className="max-w-2xl" data-testid="challan-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-xl flex items-center gap-2">
                        <Bank size={20} /> Post as challan payment
                    </DialogTitle>
                    <DialogDescription className="text-xs">
                        This debit line will settle an accrued liability instead of booking a fresh expense.
                        Bank amount <b>₹{inr(debit)}</b> · Date <b>{line?.date}</b>.
                    </DialogDescription>
                </DialogHeader>

                <KindTabs kind={kind} onChange={setKind} />

                {kind === "tds" ? (
                    <ChallanTdsPanel
                        debit={debit}
                        tdsSection={tdsSection} setTdsSection={setTdsSection}
                        tdsPeriod={tdsPeriod} setTdsPeriod={setTdsPeriod}
                        ay={ay} setAy={setAy}
                        challanNo={challanNo} setChallanNo={setChallanNo}
                        bsrCode={bsrCode} setBsrCode={setBsrCode}
                        cin={cin} setCin={setCin}
                        tdsSectionOptions={tdsSectionOptions}
                        selectedTdsSectionInfo={selectedTdsSectionInfo}
                    />
                ) : (
                    <ChallanGstPanel
                        debit={debit}
                        gstPeriod={gstPeriod} setGstPeriod={setGstPeriod}
                        challanNo={challanNo} setChallanNo={setChallanNo}
                        cpin={cpin} setCpin={setCpin}
                        bsrCode={bsrCode} setBsrCode={setBsrCode}
                        cin={cin} setCin={setCin}
                        gstPreview={gstPreview} previewBusy={previewBusy}
                        gstCash={gstCash} gstDiff={gstDiff} gstMatches={gstMatches}
                    />
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={busy} data-testid="challan-cancel">Cancel</Button>
                    {kind === "tds" ? (
                        <Button onClick={postTds} disabled={busy || debit <= 0}
                                style={{ background: "var(--se-brand)", color: "#fff" }}
                                data-testid="challan-post-tds">
                            {busy ? "Posting…" : `Post TDS challan · ₹${inr(debit)}`}
                        </Button>
                    ) : (
                        <Button onClick={postGst} disabled={busy || debit <= 0 || !gstMatches}
                                style={{ background: "var(--se-brand)", color: "#fff" }}
                                title={!gstMatches ? "Bank amount must match computed cash-to-pay for the month (± ₹1)" : ""}
                                data-testid="challan-post-gst">
                            {busy ? "Posting…" : `Post GST challan · ₹${inr(debit)}`}
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


function KindTabs({ kind, onChange }) {
    const btn = (val, testid, label) => (
        <Button
            key={val}
            size="sm"
            variant={kind === val ? "default" : "outline"}
            onClick={() => onChange(val)}
            style={kind === val ? { background: "var(--se-brand)", color: "#fff" } : {}}
            data-testid={testid}
        >
            {label}
        </Button>
    );
    return (
        <div className="flex gap-2 border-b pb-3" style={{ borderColor: "var(--se-border)" }}>
            {btn("tds", "challan-kind-tds", "TDS challan")}
            {btn("gst", "challan-kind-gst", "GST challan")}
        </div>
    );
}


// -----------------------------------------------------------------------------
// Date helpers
// -----------------------------------------------------------------------------
function defaultPeriod(dateStr) {
    // Government TDS/GST returns are for the PREVIOUS month, so default to that.
    const base = dateStr ? new Date(dateStr) : new Date();
    if (isNaN(base.getTime())) return new Date().toISOString().slice(0, 7);
    const y = base.getFullYear(); const m = base.getMonth();
    const d = new Date(y, m - 1, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function defaultAy(dateStr) {
    // Assessment year — FY runs Apr-Mar in India. Payment in April onwards
    // belongs to AY yyyy-(yy+1).
    const base = dateStr ? new Date(dateStr) : new Date();
    const y = base.getFullYear();
    const isAfterApril = base.getMonth() >= 3;
    const fyStart = isAfterApril ? y : y - 1;
    const ayStart = fyStart + 1;
    return `${ayStart}-${String(ayStart + 1).slice(-2)}`;
}
